import {
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_DRAFT,
  ORDER_EVENT_STATUS_PUBLISHED,
  ORDER_EVENT_STATUS_VOIDED
} from '@klear/constants/order-event-status'
import { PERMISSION_ENTITY_PAYMENT } from '@klear/constants'
import type { OrderEventLifecycleState } from '@klear/order-event-lifecycle/lifecycle'
import type { OrderEventStatusViewFromMaps } from '@klear/order-event-lifecycle/status-view'
import {
  PAYMENT_STATUS_CANCELED,
  PAYMENT_STATUS_DRAFT,
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_PROCESSING,
  PAYMENT_STATUS_RETURNED,
  PAYMENT_STATUS_SCHEDULED,
  PAYMENT_STATUS_SETTLED,
  COMMITTED_PAYMENT_STATUSES,
  EDITABLE_PAYMENT_STATUSES,
  SETTLED_PAYMENT_STATUSES,
  TERMINAL_FAILURE_PAYMENT_STATUSES,
  isKnownPaymentStatus,
  normalizePaymentStatus,
  type PaymentStatus
} from '@klear/payment/constants'
import { PAYMENT_STATUS_LABELS } from '@klear/payment/presentation'

export const PAYMENT_LIFECYCLE_STATE_BY_STATUS = {
  [PAYMENT_STATUS_DRAFT]: ORDER_EVENT_STATUS_DRAFT,
  [PAYMENT_STATUS_SCHEDULED]: ORDER_EVENT_STATUS_PUBLISHED,
  [PAYMENT_STATUS_PROCESSING]: ORDER_EVENT_STATUS_PUBLISHED,
  [PAYMENT_STATUS_FAILED]: ORDER_EVENT_STATUS_PUBLISHED,
  [PAYMENT_STATUS_SETTLED]: ORDER_EVENT_STATUS_COMPLETED,
  [PAYMENT_STATUS_CANCELED]: ORDER_EVENT_STATUS_VOIDED,
  [PAYMENT_STATUS_RETURNED]: ORDER_EVENT_STATUS_VOIDED
} as const satisfies Record<PaymentStatus, OrderEventLifecycleState>

function statusIsIn(status: string | null | undefined, set: readonly PaymentStatus[]): boolean {
  return set.includes(normalizePaymentStatus(status))
}

export function paymentIsVoided(status: string | null | undefined): boolean {
  return PAYMENT_LIFECYCLE_STATE_BY_STATUS[normalizePaymentStatus(status)] === ORDER_EVENT_STATUS_VOIDED
}

export function paymentIsDraft(status: string | null | undefined): boolean {
  return normalizePaymentStatus(status) === PAYMENT_STATUS_DRAFT
}

export function paymentIsScheduled(status: string | null | undefined): boolean {
  return normalizePaymentStatus(status) === PAYMENT_STATUS_SCHEDULED
}

export function paymentIsProcessing(status: string | null | undefined): boolean {
  return normalizePaymentStatus(status) === PAYMENT_STATUS_PROCESSING
}

export function paymentIsFailed(status: string | null | undefined): boolean {
  return normalizePaymentStatus(status) === PAYMENT_STATUS_FAILED
}

export function paymentHasMoved(status: string | null | undefined): boolean {
  return statusIsIn(status, SETTLED_PAYMENT_STATUSES)
}

export function paymentIsCommitted(status: string | null | undefined): boolean {
  return statusIsIn(status, COMMITTED_PAYMENT_STATUSES)
}

export function paymentIsTerminalFailure(status: string | null | undefined): boolean {
  return statusIsIn(status, TERMINAL_FAILURE_PAYMENT_STATUSES)
}

export function paymentIsTerminal(status: string | null | undefined): boolean {
  return paymentHasMoved(status) || paymentIsTerminalFailure(status)
}

export function paymentWasReturned(status: string | null | undefined): boolean {
  return normalizePaymentStatus(status) === PAYMENT_STATUS_RETURNED
}

export function paymentIsEditable(status: string | null | undefined): boolean {
  if (!isKnownPaymentStatus(status)) return false
  return statusIsIn(status, EDITABLE_PAYMENT_STATUSES)
}

export function paymentIsProviderless(payment: { stripePaymentId?: string | null }): boolean {
  return (payment.stripePaymentId ?? '').length === 0
}

export function paymentCanAmend(
  payment: { status: string; stripePaymentId?: string | null },
  options?: { correctSettledRecord?: boolean }
): boolean {
  const settledCorrection = options?.correctSettledRecord === true && paymentHasMoved(payment.status)
  return paymentIsProviderless(payment) && (paymentIsEditable(payment.status) || settledCorrection)
}

export function paymentCanRetry(payment: { status: string; stripePaymentId?: string | null }): boolean {
  return paymentIsFailed(payment.status) && (payment.stripePaymentId ?? '').length === 0
}

export type PaymentOrderEventStatusView = OrderEventStatusViewFromMaps<
  typeof PERMISSION_ENTITY_PAYMENT,
  typeof PAYMENT_LIFECYCLE_STATE_BY_STATUS,
  typeof PAYMENT_STATUS_LABELS
>
